import React from "react";

/**
 * CloudAgnostic component showcasing cloud platform compatibility
 * Displays trusted cloud platforms and partners that work with Raidu
 *
 * @returns {JSX.Element} The rendered CloudAgnostic component
 */
const CloudAgnostic: React.FC = () => {
  try {
    // Log component rendering
    console.log("Rendering CloudAgnostic component");

    // Cloud platform partners
    const cloudPartners = [
      { name: "AWS", logo: "/cloud-providers/aws-logo-480.png" },
      { name: "Google Cloud", logo: "/cloud-providers/google-cloud-480.png" },
      { name: "Microsoft Azure", logo: "/cloud-providers/azure-240.png" },
      { name: "IBM Cloud", logo: "/cloud-providers/ibm-144.png" },
      { name: "Oracle Cloud", logo: "/cloud-providers/oracle-logo-480.png" },
      { name: "DigitalOcean", logo: "/cloud-providers/digitalocean-48.png" },
    ];

    return (
      <div id="cloud-agnostic" className="inset-0">
        {/* Subtle dot pattern overlay */}
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(#6366f1 0.75px, transparent 0.75px)",
            backgroundSize: "12px 12px",
          }}
        ></div>

        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10 py-8 md:py-12">
          <div className="rounded-3xl overflow-hidden shadow-xl bg-white border border-gray-100">
            <div className="grid grid-cols-1 items-center gap-x-6 gap-y-8 lg:grid-cols-2 p-6 sm:p-8 lg:p-10">
              <div className="mx-auto w-full max-w-xl lg:mx-0">
                <div className="inline-block mb-4">
                  <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent text-sm font-medium px-3 py-1 rounded-full bg-opacity-10 border border-indigo-200 bg-indigo-50">
                    Multi-Cloud Compatible
                  </span>
                </div>
                <h2 className="text-4xl font-semibold tracking-tight bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-700 bg-clip-text text-transparent sm:text-5xl">
                  Cloud-Agnostic AI Security
                </h2>
                <p className="mt-6 text-lg text-gray-600">
                  Raidu's AI control plane works seamlessly across all major
                  cloud providers, ensuring consistent security, governance, and
                  compliance regardless of where your AI systems are deployed.
                </p>
                <div className="mt-8 flex items-center gap-x-6">
                  <a
                    href="/booking"
                    className="rounded-full bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-3 text-sm font-semibold text-white shadow-md hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 transform hover:scale-105 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log(
                          "CloudAgnostic - Get Started button clicked"
                        );
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Get Started button click:",
                          error
                        );
                      }
                    }}
                  >
                    Get Started
                  </a>
                  <a
                    href="/booking"
                    className="text-sm font-semibold text-indigo-600 hover:text-indigo-500 transition-colors"
                    onClick={() => {
                      try {
                        // Log button click
                        console.log(
                          "CloudAgnostic - Learn More button clicked"
                        );
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Learn More button click:",
                          error
                        );
                      }
                    }}
                  >
                    Learn More <span aria-hidden="true">&rarr;</span>
                  </a>
                </div>
              </div>
              <div className="relative mx-auto w-full max-w-xl lg:mx-0 lg:max-w-none lg:pl-8">
                {/* Decorative gradient circle */}
                <div className="absolute -right-10 -top-10 w-40 h-40 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 rounded-full blur-2xl -z-10"></div>
                {/* Decorative gradient circle */}
                <div className="absolute -left-10 -bottom-10 w-40 h-40 bg-gradient-to-br from-blue-200/40 to-indigo-200/40 rounded-full blur-2xl -z-10"></div>

                {/* Pyramid layout for logos */}
                {/* First row - 3 logos centered */}
                <div className="flex justify-center gap-5 mb-5">
                  {cloudPartners.slice(0, 3).map((partner, index) => (
                    <div
                      key={index}
                      className="bg-white rounded-lg p-3 shadow-[0_4px_10px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(99,102,241,0.15)] transition-all duration-300 flex items-center justify-center h-20 w-20 sm:w-24 md:w-28 hover:scale-110 hover:-translate-y-1 cursor-pointer"
                    >
                      <img
                        alt={partner.name}
                        src={partner.logo}
                        width={140}
                        height={48}
                        className="max-h-12 w-full object-contain transition-all duration-300"
                        onError={(e) => {
                          try {
                            // Log error loading image
                            console.error(
                              `Error loading cloud partner logo: ${partner.name}`,
                              e.currentTarget.src
                            );
                            // Set fallback image
                            e.currentTarget.src =
                              "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                          } catch (error) {
                            // Log any errors during error handling
                            console.error(
                              "Error in image error handling:",
                              error
                            );
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Second row - 2 logos centered */}
                <div className="flex justify-center gap-5 mb-5">
                  {cloudPartners.slice(3, 5).map((partner, index) => (
                    <div
                      key={index + 3}
                      className="bg-white rounded-lg p-3 shadow-[0_4px_10px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(99,102,241,0.15)] transition-all duration-300 flex items-center justify-center h-20 w-20 sm:w-24 md:w-28 hover:scale-110 hover:-translate-y-1 cursor-pointer"
                    >
                      <img
                        alt={partner.name}
                        src={partner.logo}
                        width={140}
                        height={48}
                        className="max-h-12 w-full object-contain transition-all duration-300"
                        onError={(e) => {
                          try {
                            // Log error loading image
                            console.error(
                              `Error loading cloud partner logo: ${partner.name}`,
                              e.currentTarget.src
                            );
                            // Set fallback image
                            e.currentTarget.src =
                              "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                          } catch (error) {
                            // Log any errors during error handling
                            console.error(
                              "Error in image error handling:",
                              error
                            );
                          }
                        }}
                      />
                    </div>
                  ))}
                </div>

                {/* Third row - 1 logo centered */}
                <div className="flex justify-center mb-5">
                  <div className="bg-white rounded-lg p-3 shadow-[0_4px_10px_rgba(0,0,0,0.08)] hover:shadow-[0_8px_20px_rgba(99,102,241,0.15)] transition-all duration-300 flex items-center justify-center h-20 w-20 sm:w-24 md:w-28 hover:scale-110 hover:-translate-y-1 cursor-pointer">
                    <img
                      alt={cloudPartners[5].name}
                      src={cloudPartners[5].logo}
                      width={140}
                      height={48}
                      className="max-h-12 w-full object-contain transition-all duration-300"
                      onError={(e) => {
                        try {
                          // Log error loading image
                          console.error(
                            `Error loading cloud partner logo: ${cloudPartners[5].name}`,
                            e.currentTarget.src
                          );
                          // Set fallback image
                          e.currentTarget.src =
                            "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                        } catch (error) {
                          // Log any errors during error handling
                          console.error(
                            "Error in image error handling:",
                            error
                          );
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Many More text centered */}
                <div className="flex justify-center mt-6">
                  <div
                    className="text-center cursor-pointer group"
                    onClick={() => {
                      try {
                        // Log click on Many More text
                        console.log("Many More cloud partners text clicked");
                      } catch (error) {
                        // Log any errors during click handling
                        console.error(
                          "Error handling Many More text click:",
                          error
                        );
                      }
                    }}
                  >
                    <p className="text-gray-500 font-medium group-hover:text-gray-700 transition-colors flex items-center gap-1">
                      <span>Many more</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error("Error rendering CloudAgnostic component:", error);

    // Return error state
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-base font-semibold text-indigo-600">Error</p>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Failed to load cloud partners section
            </h2>
            <p className="mt-4 text-lg text-gray-500">
              Please refresh the page or try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }
};

export default CloudAgnostic;
